import type { DeviceType } from '~/types/device-type'
import type {
  IIapFeatureResource,
  IIapGraph,
  IIapResource,
  IAP_SUB_TYPE,
  IInAppFeature,
  IInAppFeaturesGroup,
  TInAppFeaturePayload,
  IInAppOffer,
  TInAppOfferPayload,
  IInApp,
  IInAppFiltersCount,
  TInAppListingPayload,
  TInAppFiltersPayload,
  IInAppPrice,
  TInAppFeaturesGroupPayload,
  TCreateOfferPayload,
  TUpdateOfferPayload,
} from '~/types/iap'

import config from '~/config'
import { request } from '~/utils/request'

export const getWorkflowGraph = (appId: string): Promise<IIapGraph> => {
  return request({
    url: `${config.VERSIONS_ADMIN}/application/${appId}/iap-packs/workflow-graph
    `,
  })
}

export const getResource = (appId: string, resourceId: string): Promise<IIapResource[] | IIapFeatureResource> => {
  return request({
    url: `${config.VERSIONS_ADMIN}/application/${appId}/iap-packs/resource/${resourceId}`,
  })
}

export const updateSubType = ({
  appId,
  iapId,
  subType,
}: {
  appId: string
  iapId: string
  subType: IAP_SUB_TYPE
}): Promise<void> => {
  return request({
    data: {
      inapp_id: iapId,
      sub_type: subType,
    },
    method: 'POST',
    url: `${config.VERSIONS_ADMIN}/application/${appId}/iap/product`,
  })
}

export const refreshIap = (appId: string): Promise<void> => {
  return request<void>({
    method: 'POST',
    url: `${config.VERSIONS_ADMIN}/application/${appId}/iap/refresh`,
  })
}

///////////////////// REWORK IAP API BELOW /////////////////////

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/in-apps */
export const getInAppsList = ({
  apps,
  freeTrial,
  limit,
  page,
  period,
  priceRange,
  searchQuery,
  type,
}: TInAppListingPayload): Promise<IPaginatedResponse<IInApp>> => {
  return request({
    data: {
      apps,
      free_trial: freeTrial,
      limit,
      max_price: priceRange.max,
      min_price: priceRange.min,
      page,
      period,
      query: searchQuery,
      type,
    },
    method: 'POST',
    url: `${config.IAP_API}/in-apps`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/in-apps/filters-data */
export const listFiltersData = ({
  apps,
  freeTrial,
  period,
  priceRange,
  searchQuery,
}: TInAppFiltersPayload): Promise<IInAppFiltersCount> => {
  return request({
    data: {
      apps,
      free_trial: freeTrial,
      max_price: priceRange.max,
      min_price: priceRange.min,
      period,
      query: searchQuery,
    },
    method: 'POST',
    url: `${config.IAP_API}/in-apps/filters-data`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/app-key/-appKey-/device-type/-deviceType-/in-apps/refresh */
export const refreshInAppsForApp = ({
  appKey,
  deviceType,
}: {
  appKey: string
  deviceType: DeviceType
}): Promise<void> => {
  return request({
    method: 'POST',
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/in-apps/refresh`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/in-apps/daily-refresh */
export const inAppsDailyRefresh = (): Promise<void> => {
  return request({
    method: 'POST',
    url: `${config.IAP_API}/in-apps/daily-refresh`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/app-key/-appKey-/device-type/-deviceType-/sku/-sku-/in-apps/prices */
export const getPrices = ({
  appKey,
  deviceType,
  sku,
}: {
  appKey: string
  deviceType: DeviceType
  sku: string
}): Promise<IInAppPrice[]> => {
  return request({
    method: 'POST',
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/sku/${sku}/in-apps/prices`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#get-/app-key/-appKey-/device-type/-deviceType-/features */
export const getFeaturesList = ({
  appKey,
  deviceType,
}: {
  appKey: string
  deviceType: DeviceType
}): Promise<IPaginatedResponse<IInAppFeature>> => {
  return request({
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/features`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#get-/features/features-ids */
export const getFeaturesIdsList = (searchQuery?: string): Promise<string[]> => {
  return request({
    params: { q: searchQuery },
    url: `${config.IAP_API}/features/features-ids`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/app-key/-appKey-/device-type/-deviceType-/features */
export const createFeature = ({ appKey, deviceType, featureId }: TInAppFeaturePayload): Promise<void> => {
  return request({
    method: 'POST',
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/features/${featureId}`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#delete-/app-key/-appKey-/device-type/-deviceType-/features */
export const deleteFeature = ({ appKey, deviceType, featureId }: TInAppFeaturePayload): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/features/${featureId}`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#get-/app-key/-appKey-/device-type/-deviceType-/features-group */
export const getFeaturesGroupList = ({
  appKey,
  deviceType,
  featureId,
  searchQuery,
}: {
  appKey: string
  deviceType: DeviceType
  featureId?: string
  searchQuery?: string
}): Promise<IPaginatedResponse<IInAppFeaturesGroup>> => {
  return request({
    params: {
      feature_id: featureId,
      q: searchQuery,
    },
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/features-group`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/offers/list */
export const getOffersList = ({
  appsInfos,
  limit,
  page,
  searchQuery,
}: {
  appsInfos: TInAppOfferPayload
  limit: number
  page: number
  searchQuery: string
}): Promise<IPaginatedResponse<IInAppOffer>> => {
  return request({
    data: appsInfos,
    method: 'POST',
    params: {
      limit,
      page,
      q: searchQuery,
    },
    url: `${config.IAP_API}/offers/list`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#get-/offers/-offerID- */
export const getOffer = ({ offerId }: { offerId: string }): Promise<IPaginatedResponse<IInAppOffer>> => {
  return request({
    url: `${config.IAP_API}/offers/${offerId}`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/offers */
export const createOffer = (data: TCreateOfferPayload): Promise<void> => {
  return request({
    data,
    method: 'POST',
    url: `${config.IAP_API}/offers`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#put-/offers/-offerID- */
export const updateOffer = (data: TUpdateOfferPayload): Promise<IInAppOffer> => {
  return request({
    data,
    method: 'PUT',
    url: `${config.IAP_API}/offers/${data.offer_id}`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#delete-/offers/-offerID- */
export const deleteOffer = (offerId: string): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.IAP_API}/offers/${offerId}`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/in-apps/skus */
export const getSkusIdsList = (searchQuery?: string): Promise<string[]> => {
  return request({
    method: 'POST',
    params: { q: searchQuery },
    url: `${config.IAP_API}/in-apps/skus`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#post-/app-key/-appKey-/device-type/-deviceType-/features-group */
export const createFeaturesGroup = ({
  appKey,
  deviceType,
  featureIds,
  name,
}: TInAppFeaturesGroupPayload): Promise<void> => {
  return request({
    data: {
      feature_ids: featureIds,
      name,
    },
    method: 'POST',
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/features-group`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#put-/app-key/-appKey-/device-type/-deviceType-/features-group/-featureGroupID- */
export const updateFeaturesGroup = ({
  appKey,
  deviceType,
  featureGroupId,
  featureIds,
  name,
}: TInAppFeaturesGroupPayload & { featureGroupId: string }): Promise<IInAppFeaturesGroup> => {
  return request({
    data: {
      feature_ids: featureIds,
      name,
    },
    method: 'PUT',
    url: `${config.IAP_API}/app-key/${appKey}/device-type/${deviceType}/features-group/${featureGroupId}`,
  })
}

/** @doc https://api-dev-europe-west2-45305736168.europe-west2.run.app/#delete-/features-group/-featureGroupID- */
export const deleteFeaturesGroup = ({ featureGroupId }: { featureGroupId: string }): Promise<void> => {
  return request({
    method: 'DELETE',
    url: `${config.IAP_API}/features-group/${featureGroupId}`,
  })
}
